import React, { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState, ReactNode } from 'react';
import type { SessionKind, MessageRole } from "../types/entities";
import { postReceiptThreadMessage, toThreadPayload } from '../services/materialReceiptService';
import type { ProxyTask } from '../data/proxyWorkflowRegistry';

export interface ChatSessionLocal {
  id: string;
  title: string;
  kind: SessionKind;
  jobNum?: string;
  // Every job number this thread has ever touched — a receipt can cover
  // several jobs at once, and it's not guaranteed which one a Proxy-side
  // session was first opened against, so merge lookups match on overlap
  // against this accumulated union rather than the single `jobNum` above.
  jobNums?: string[];
  client?: string;
  caEmail?: any;
  caWorkflowComplete?: boolean;
  caDraftText?: string;
  caApproved?: boolean;
  advisorEmail?: any;
  advisorWorkflowComplete?: boolean;
  advisorDraftText?: string;
  advisorApproved?: boolean;
  // Proxy Intake Queue → ACE workflow (any registry entry — see
  // data/proxyWorkflowRegistry.ts). Unlike the CA/advisor email workflows,
  // every Proxy workflow's calculation is synchronous (no LLM draft step),
  // so only the approval outcome needs to survive a session revisit.
  proxyTask?: ProxyTask;
  proxyTaskApproved?: boolean;
  // Material-intake link: ties a thread to its MaterialReceipt (camera/queue flow).
  receiptId?: string;
  scan?: string;
  category?: string;
  receiptValidated?: boolean;
  createdAt: string;
  updatedAt: string;
  preview: string;
}

export interface ChatMessageLocal {
  id?: string;
  role: MessageRole | "user" | "agent";
  content: string;
  ts?: string;
  // A frozen job-matching/RDQ card as it looked at this point in the thread —
  // written when a task is approved, or when a still-pending task is about to
  // be replaced by a new one for the same job — so history stays a faithful
  // record even after the live session.proxyTask moves on.
  proxyTaskSnapshot?: ProxyTask;
  proxyTaskSnapshotApproved?: boolean;
  [key: string]: unknown;
}

interface ChatsContextValue {
  sessions: ChatSessionLocal[];
  activeId: string | null;
  msgsById: Record<string, ChatMessageLocal[]>;
  msgs: ChatMessageLocal[];
  fmtTime: (iso: string) => string;
  setActive: (id: string) => void;
  ensureSession: () => string;
  newSession: (meta?: Partial<ChatSessionLocal>) => string;
  updateSession: (id: string, patch: Partial<ChatSessionLocal>) => void;
  deleteSession: (id: string) => void;
  appendMsg: (sessionId: string, msg: ChatMessageLocal) => void;
  // Hydration from the server thread — sets messages without re-syncing them.
  replaceMsgs: (sessionId: string, msgs: ChatMessageLocal[]) => void;
}

const ChatsContext = createContext<ChatsContextValue | null>(null);

const LS_KEY = 'opsai.chats.v1';

function safeParse(json, fallback) {
  try {
    const v = JSON.parse(json);
    return v ?? fallback;
  } catch {
    return fallback;
  }
}

function uid() {
  return `${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 8)}`;
}

function nowIso() {
  return new Date().toISOString();
}

function buildTitle(meta: Record<string, any> = {}) {
  if (meta.scan) return `Scan #${meta.scan}`;
  if (meta.jobNum) return `Job #${meta.jobNum}`;
  if (meta.kind === 'upload') return 'Scan upload';
  if (meta.kind === 'camera') return 'Camera';
  if (meta.kind === 'tag') return 'Job tag';
  if (meta.kind === 'email') return `CA · ${meta.client || 'Email'}`;
  if (meta.kind === 'advisor-email') return `Reg · ${meta.client || 'Advisor'}`;
  if (meta.kind === 'proxy-task') return `Proxy · ${meta.client || 'RDQ Order'}`;
  return 'New chat';
}

function fmtTime(iso) {
  if (!iso) return '';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '';
  return d.toLocaleString([], { month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' });
}

export function ChatsProvider({ children }: { children: ReactNode }) {
  const initialState = useMemo(() => {
    if (typeof window === 'undefined') return { sessions: [], activeId: null, msgsById: {} };
    const raw = window.localStorage.getItem(LS_KEY);
    const data = safeParse(raw, null);
    if (data?.sessions && data?.msgsById) {
      return {
        sessions: Array.isArray(data.sessions) ? data.sessions : [],
        msgsById: (typeof data.msgsById === 'object' && data.msgsById) ? data.msgsById : {},
        activeId: data.activeId || null,
      };
    }
    return { sessions: [], activeId: null, msgsById: {} };
  }, []);

  const [sessions, setSessions] = useState(initialState.sessions);
  const [activeId, setActiveId] = useState(initialState.activeId);
  const [msgsById, setMsgsById] = useState(initialState.msgsById);
  const sessionsRef = useRef([]);
  // Synchronous sessionId → receiptId map so appendMsg can sync receipt-linked
  // messages to the server thread even in the same tick the session was created.
  const receiptBySessionRef = useRef<Record<string, string | undefined>>(
    Object.fromEntries((initialState.sessions || []).map((s: any) => [s.id, s.receiptId])),
  );

  useEffect(() => {
    if (typeof window === 'undefined') return;
    const payload = JSON.stringify({ sessions, activeId, msgsById });
    window.localStorage.setItem(LS_KEY, payload);
  }, [sessions, activeId, msgsById]);

  useEffect(() => {
    sessionsRef.current = sessions;
  }, [sessions]);

  const setActive = useCallback((id) => {
    setActiveId(id);
  }, []);

  const ensureSession = useCallback(() => {
    if (activeId) return activeId;
    const id = uid();
    const t = nowIso();
    const s = { id, title: 'New chat', kind: 'manual', createdAt: t, updatedAt: t, preview: '' };
    setSessions(prev => [s, ...prev]);
    setMsgsById(prev => ({ ...prev, [id]: [] }));
    setActiveId(id);
    return id;
  }, [activeId]);

  const newSession = useCallback((meta: Record<string, any> = {}) => {
    const id = uid();
    const t = nowIso();
    const title = buildTitle(meta);
    const s = {
      id,
      title,
      kind: meta.kind || 'manual',
      jobNum: meta.jobNum,
      jobNums: meta.jobNums,
      client: meta.client,
      caEmail: meta.caEmail,
      advisorEmail: meta.advisorEmail,
      proxyTask: meta.proxyTask,
      receiptId: meta.receiptId,
      scan: meta.scan,
      category: meta.category,
      createdAt: t,
      updatedAt: t,
      preview: '',
    };
    receiptBySessionRef.current[id] = meta.receiptId;
    setSessions(prev => [s, ...prev.filter(x => x.id !== id)]);
    setMsgsById(prev => ({ ...prev, [id]: [] }));
    setActiveId(id);
    return id;
  }, []);

  const updateSession = useCallback((id, patch: Record<string, any> = {}) => {
    if (patch.receiptId) receiptBySessionRef.current[id] = patch.receiptId;
    setSessions(prev => prev.map(s => s.id !== id ? s : { ...s, ...patch }));
  }, []);

  // Hydrate a session's messages from the server thread (no re-sync).
  const replaceMsgs = useCallback((sessionId, msgs) => {
    if (!sessionId) return;
    setMsgsById(prev => ({ ...prev, [sessionId]: Array.isArray(msgs) ? msgs : [] }));
  }, []);

  const deleteSession = useCallback((id) => {
    if (!id) return;
    delete receiptBySessionRef.current[id];

    setSessions(prev => prev.filter(s => s.id !== id));

    setActiveId(prevActive => {
      if (prevActive !== id) return prevActive;
      const remaining = (sessionsRef.current || []).filter(s => s.id !== id);
      return remaining[0]?.id || null;
    });

    setMsgsById(prev => {
      if (!prev || typeof prev !== 'object') return prev;
      // eslint-disable-next-line no-unused-vars
      const { [id]: _omit, ...rest } = prev;
      return rest;
    });
  }, []);

  const appendMsg = useCallback((sessionId, msg) => {
    if (!sessionId) return;
    const t = nowIso();
    const content = (msg?.content || '').toString();
    const preview = content.replace(/<[^>]*>/g, '').trim().slice(0, 64);

    setMsgsById(prev => {
      const cur = Array.isArray(prev[sessionId]) ? prev[sessionId] : [];
      return { ...prev, [sessionId]: [...cur, { ...msg, ts: msg?.ts || t }] };
    });

    // Receipt-linked sessions mirror every message to the server thread — the
    // receipt's shared memory log. Fire-and-forget: the local thread is the
    // cache; a failed sync never blocks the UI.
    const receiptId = receiptBySessionRef.current[sessionId];
    if (receiptId) {
      postReceiptThreadMessage(receiptId, toThreadPayload(msg)).catch(() => {});
    }

    setSessions(prev => {
      const next = prev.map(s => {
        if (s.id !== sessionId) return s;
        return { ...s, updatedAt: t, preview: preview || s.preview };
      });
      next.sort((a, b) => new Date(b.updatedAt || 0).getTime() - new Date(a.updatedAt || 0).getTime());
      return next;
    });
  }, []);

  const msgs = useMemo(() => {
    if (!activeId) return [];
    return Array.isArray(msgsById[activeId]) ? msgsById[activeId] : [];
  }, [activeId, msgsById]);

  const value = useMemo(() => ({
    sessions,
    activeId,
    msgsById,
    msgs,
    fmtTime,
    setActive,
    ensureSession,
    newSession,
    updateSession,
    deleteSession,
    appendMsg,
    replaceMsgs,
  }), [sessions, activeId, msgsById, msgs, setActive, ensureSession, newSession, updateSession, deleteSession, appendMsg, replaceMsgs]);

  return (
    <ChatsContext.Provider value={value}>
      {children}
    </ChatsContext.Provider>
  );
}

export function useChats(): ChatsContextValue {
  const ctx = useContext(ChatsContext);
  if (!ctx) throw new Error('useChats must be used inside ChatsProvider');
  return ctx;
}

